import React from 'react';
import fr from 'facet-rest';
import { FDStack } from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { FormSection } from 'Components/ComponentLibrary/Molecules/FormSection';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { ProfileImageUploader } from 'Components/ComponentLibrary/Organisms/ProfileImageUploader';
import PropTypes from 'prop-types';
import { FDInputRow } from 'Packages/FacetDesign/Components/Experimental/fdInputRow';
import VerifyIdentity from 'Components/ComponentLibrary/Organisms/ClientDetailForm/VerifyIdentity/VerifyIdentity';
import { useRefreshModalCollectionCleared } from 'Hooks/useRefreshEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';

// ClientDetailForm could be used on a Card or in a dialog
export const ClientDetailForm = ({
  onProfileImageUpload,
}) => {
  const { refreshPageContentCollection } = usePageContentContext();

  const { resource, consumeAttr } = useMetaLayoutContext();

  useRefreshModalCollectionCleared((evt, refreshPageContentCollectionDependency) => {
    // In general, if you close a modal, we should refresh to show what you just entered.
    // However, when you complete the fill-in-household-info item, we redirect you to the dashboard - in that situation, we shouldn't refresh this page
    if (evt.detail.modalCollection.meta().type() !== 'ClientDashboardPage') {
      refreshPageContentCollectionDependency();
    }
  }, [refreshPageContentCollection]);

  // if we have kycIdentityVerification rel, then we should render the VerifyIdentity component
  const VerificationComponent = resource.withRelationship(fr.relationships.kycIdentityVerification.name,
    (rel) => <VerifyIdentity relationship={rel} />,
    () => null);

  return (
    <FDStack direction={'column'} spacing={3}>
      {VerificationComponent}
      <FormSection
        title={(
          <FDStack direction="row" width="100%" spacing={3} wrap justifyContent="space-between">
            Basic Information
            <MetaControlField attr={'isIdentityVerified'} />
          </FDStack>
        )}
      >
        <FDInputRow>
          <MetaControlField attr={'firstName'} flex={1} />
          <MetaControlField attr={'lastName'} flex={1} />
          <MetaControlField attr={'preferredName'} flex={1} />
          <MetaControlField attr={'birthDate'} flex={1} />
        </FDInputRow>
        {resource.withRelationship(fr.relationships.profileImage.name, (rel) => {
          return (
            <FDStack justifyContent={'flex-start'}>
              <ProfileImageUploader
                profileImageRelationship={rel}
                onSuccess={onProfileImageUpload}
              />
            </FDStack>
          );
        }, () => null)}
      </FormSection>
      <FormSection title={'Other Information'}>
        <FDInputRow>
          <MetaControlField attr={'emailAddress'} flex={1} />
          <MetaControlField attr={'relationship'} flex={1} />
        </FDInputRow>
      </FormSection>
      <FormSection title={'Legal Information'}>
        <FDInputRow>
          <MetaControlField attr={'citizenshipDetails'} flex={1} />
          <MetaControlField attr={'socialSecurityNumber'} flex={1} />
        </FDInputRow>
      </FormSection>

      {resource.attr('isIdentityVerified') === 'true' && (
        <>
          <FormSection title={consumeAttr('physicalAddressTitle')}>
            <FDInputRow>
              <MetaControlField attr={'physicalAddressLine1'} flex={1} />
              <MetaControlField attr={'physicalAddressLine2'} flex={1} />
              <MetaControlField attr={'physicalCity'} flex={1} />
              <MetaControlField attr={'physicalState'} flex={1} />
              <MetaControlField attr={'physicalZipCode'} flex={1} />
            </FDInputRow>
          </FormSection>
          {consumeAttr('mailingAddressTitle') ? (
            <FormSection title={consumeAttr('mailingAddressTitle')}>
              <MetaControlField attr={'mailingAddressSameAsPhysical'} flex={1} />
              <FDInputRow>
                <MetaControlField attr={'mailingAddressLine1'} flex={1} />
                <MetaControlField attr={'mailingAddressLine2'} flex={1} />
                <MetaControlField attr={'mailingCity'} flex={1} />
                <MetaControlField attr={'mailingState'} flex={1} />
                <MetaControlField attr={'mailingZipCode'} flex={1} />
              </FDInputRow>
            </FormSection>
          ) : null}
        </>
      )}
    </FDStack>
  );
};

ClientDetailForm.defaultProps = {
  onProfileImageUpload: undefined,
};

ClientDetailForm.propTypes = {
  onProfileImageUpload: PropTypes.func,
};
